import {
  useCallback,
  useMemo,
  type FC,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";
import {
  AimOutlined,
  InfoCircleOutlined,
  LinkOutlined,
  PieChartOutlined,
} from "@ant-design/icons";
import { Tooltip } from "antd";

import { AppStrings } from "../../../../utils/constants/AppStrings";
import { Button, Form, RadioCard } from "../../../shared";

import CpaConditions from "./CpaConditions";
import RevShareConditions from "./RevShareConditions";

import styles from "./Payment.module.css";

export type PaymentType = "cpa" | "rev_share" | "hybrid";
export type CpaActionType = "first_time_deposits" | "user_signups";
export type RevenueSource = "sportsbook_casino" | "sportsbook" | "casino";

export interface RevShareRow {
  minProfit?: number | string | null;
  revenuePct?: number | string | null;
}

export interface PaymentStepValue {
  type: PaymentType;
  cpa?: {
    actionType?: CpaActionType;
    paymentPerAction?: number | string | null;
  };
  revShare?: {
    revenueSource?: RevenueSource;
    sportsbook?: RevShareRow;
    casino?: RevShareRow;
  };
}

const PAYMENT_INITIAL_VALUES: Partial<PaymentStepValue> = { type: "cpa" };

const REV_SHARE_FIELD_PATHS: ReadonlyArray<(string | number)[]> = [
  ["revShare", "revenueSource"],
  ["revShare", "sportsbook", "minProfit"],
  ["revShare", "sportsbook", "revenuePct"],
  ["revShare", "casino", "minProfit"],
  ["revShare", "casino", "revenuePct"],
];

const CPA_FIELD_PATHS: ReadonlyArray<(string | number)[]> = [
  ["cpa", "actionType"],
  ["cpa", "paymentPerAction"],
];

const toNumber = (raw: unknown): number | null => {
  if (raw === null || raw === undefined || raw === "") return null;
  const n = typeof raw === "number" ? raw : Number(raw);
  return Number.isFinite(n) ? n : null;
};

const isRowValid = (row?: RevShareRow): boolean => {
  if (!row) return false;
  const profit = toNumber(row.minProfit);
  const pct = toNumber(row.revenuePct);
  return (
    profit !== null &&
    profit > 0 &&
    pct !== null &&
    Number.isInteger(pct) &&
    pct > 0 &&
    pct <= 100
  );
};

const isCpaValid = (cpa?: PaymentStepValue["cpa"]): boolean => {
  if (!cpa?.actionType) return false;
  const amount = toNumber(cpa.paymentPerAction);
  return amount !== null && amount > 0;
};

// Only the rows for the chosen source are shown and required — the
// payload mapper nulls the non-selected product's fields anyway.
const isRevShareValid = (rs?: PaymentStepValue["revShare"]): boolean => {
  if (!rs?.revenueSource) return false;
  if (rs.revenueSource === "sportsbook_casino") {
    return isRowValid(rs.sportsbook) && isRowValid(rs.casino);
  }
  if (rs.revenueSource === "sportsbook") return isRowValid(rs.sportsbook);
  if (rs.revenueSource === "casino") return isRowValid(rs.casino);
  return false;
};

export const isPaymentValid = (values?: Partial<PaymentStepValue>): boolean => {
  if (!values) return false;
  if (values.type === "cpa") return isCpaValid(values.cpa);
  if (values.type === "rev_share") return isRevShareValid(values.revShare);
  if (values.type === "hybrid") {
    return isCpaValid(values.cpa) && isRevShareValid(values.revShare);
  }
  return false;
};

export type PaymentProps = {
  initialValues?: Partial<PaymentStepValue>;
  onValuesChange?: (values: Partial<PaymentStepValue>) => void;
  onContinue?: (values: PaymentStepValue) => void;
  hideFooter?: boolean;
};

const Payment: FC<PaymentProps> = ({
  initialValues,
  onValuesChange,
  onContinue,
  hideFooter = false,
}) => {
  const [form] = Form.useForm<Partial<PaymentStepValue>>();

  const mergedInitialValues = useMemo(
    () => ({ ...PAYMENT_INITIAL_VALUES, ...initialValues }),
    [initialValues],
  );

  const canContinue = Form.useWatch(
    (values: Partial<PaymentStepValue> | undefined) => isPaymentValid(values),
    form,
  );

  const type = Form.useWatch("type", form) as PaymentType | undefined;
  const revenueSource = Form.useWatch(["revShare", "revenueSource"], form) as
    | RevenueSource
    | undefined;

  const handleValuesChange = useCallback(
    (_changed: Partial<PaymentStepValue>, all: Partial<PaymentStepValue>) => {
      onValuesChange?.(all);
    },
    [onValuesChange],
  );

  const handleTypeSelect = useCallback(
    (value: PaymentType) => {
      const current = form.getFieldValue("type") as PaymentType | undefined;
      if (current === value) return;

      form.setFieldValue("type", value);

      // hybrid keeps both sub-forms, so we only clear the opposite branch
      // when switching to a single-type plan.
      const pathsToClear =
        value === "cpa"
          ? REV_SHARE_FIELD_PATHS
          : value === "rev_share"
            ? CPA_FIELD_PATHS
            : [];
      if (pathsToClear.length > 0) {
        form.setFields(
          pathsToClear.map((name) => ({
            name: [...name],
            value: undefined,
          })) as Parameters<typeof form.setFields>[0],
        );
      }

      // antd leaves the cleared nested paths in the snapshot as
      // `{ sportsbook: { minProfit: undefined } }` rather than removing the
      // parent key, so strip the discarded branch ourselves before mirroring
      // up to the wizard's draft.
      const snapshot = form.getFieldsValue() as Partial<PaymentStepValue>;
      if (value === "cpa") {
        delete snapshot.revShare;
      } else if (value === "rev_share") {
        delete snapshot.cpa;
      }

      onValuesChange?.(snapshot);
    },
    [form, onValuesChange],
  );

  const handleRadioGroupKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      const navKeys = [
        "ArrowRight",
        "ArrowDown",
        "ArrowLeft",
        "ArrowUp",
        "Home",
        "End",
      ];
      if (!navKeys.includes(event.key)) return;
      event.preventDefault();

      const radios = Array.from(
        event.currentTarget.querySelectorAll<HTMLButtonElement>(
          '[role="radio"]:not([disabled])',
        ),
      );
      if (radios.length === 0) return;

      const activeIdx = radios.findIndex((r) => r === document.activeElement);
      const last = radios.length - 1;

      let nextIdx: number;
      if (event.key === "Home") nextIdx = 0;
      else if (event.key === "End") nextIdx = last;
      else if (activeIdx === -1) nextIdx = 0;
      else {
        const delta =
          event.key === "ArrowRight" || event.key === "ArrowDown" ? 1 : -1;
        nextIdx = (activeIdx + delta + radios.length) % radios.length;
      }

      const target = radios[nextIdx];
      target.focus();

      target.click();
    },
    [],
  );

  const handleFinish = useCallback(
    (values: Partial<PaymentStepValue>) => {
      if (!isPaymentValid(values)) return;
      onContinue?.(values as PaymentStepValue);
    },
    [onContinue],
  );

  return (
    <Form<Partial<PaymentStepValue>>
      form={form}
      initialValues={mergedInitialValues}
      onFinish={handleFinish}
      onValuesChange={handleValuesChange}
      className={styles.form}
      data-testid="new-affiliate-payment-form"
    >
      <Form.Item name="type" hidden noStyle>
        <input type="hidden" />
      </Form.Item>

      <section
        className={styles.section}
        aria-labelledby="new-affiliate-payment-type-title"
      >
        <div className={styles.sectionHeader}>
          <h2
            id="new-affiliate-payment-type-title"
            className={styles.sectionTitle}
          >
            {AppStrings.PAYMENT_TYPE}
          </h2>
          <Tooltip title={AppStrings.CHOOSE_HOW_THIS_AFFILIATE}>
            <InfoCircleOutlined
              className={styles.titleInfoIcon}
              aria-label={AppStrings.ABOUT_PAYMENT_TYPE}
              data-testid="new-affiliate-payment-type-info"
            />
          </Tooltip>
        </div>
        <p className={styles.hint}>{AppStrings.CHOOSE_TYPE}</p>

        <div
          className={styles.cardGrid}
          role="radiogroup"
          aria-labelledby="new-affiliate-payment-type-title"
          onKeyDown={handleRadioGroupKeyDown}
        >
          <RadioCard<PaymentType>
            value="cpa"
            selected={type === "cpa"}
            icon={<AimOutlined />}
            title={AppStrings.AFFILIATE_PAYMENT_CPA}
            description={AppStrings.PAY_A_FIXED_AMOUNT}
            onSelect={handleTypeSelect}
            tabIndex={type === "cpa" ? 0 : -1}
            data-testid="new-affiliate-payment-card-cpa"
          />
          <RadioCard<PaymentType>
            value="rev_share"
            selected={type === "rev_share"}
            icon={<PieChartOutlined />}
            title={AppStrings.AFFILIATE_PAYMENT_REV_SHARE}
            description={AppStrings.SHARE_A_PERCENTAGE_OF}
            onSelect={handleTypeSelect}
            tabIndex={type === "rev_share" ? 0 : -1}
            data-testid="new-affiliate-payment-card-rev-share"
          />

          <RadioCard<PaymentType>
            value="hybrid"
            selected={type === "hybrid"}
            icon={<LinkOutlined />}
            title={AppStrings.AFFILIATE_PAYMENT_HYBRID}
            description={
              AppStrings.AFFILIATE_PAYMENT_HYBRID_DESCRIPTION_LONG
            }
            onSelect={handleTypeSelect}
            tabIndex={type === "hybrid" ? 0 : -1}
            data-testid="new-affiliate-payment-card-hybrid"
          />
        </div>
      </section>

      <div className={styles.divider} role="separator" aria-hidden="true" />

      <section
        className={styles.section}
        aria-labelledby="new-affiliate-payment-conditions-title"
      >
        <div className={styles.sectionHeader}>
          <h2
            id="new-affiliate-payment-conditions-title"
            className={styles.sectionTitle}
          >
            {AppStrings.CONDITIONS_TITLE}
          </h2>
          <Tooltip title={AppStrings.SET_THE_RULES_THAT}>
            <InfoCircleOutlined
              className={styles.titleInfoIcon}
              aria-label={AppStrings.ABOUT_CONDITIONS}
              data-testid="new-affiliate-payment-conditions-info"
            />
          </Tooltip>
        </div>

        {type === "rev_share" && (
          <RevShareConditions revenueSource={revenueSource} />
        )}
        {type === "cpa" && <CpaConditions />}
        {type === "hybrid" && (
          <>
            <h3 className={styles.subSectionTitle}>
              {AppStrings.AFFILIATE_PAYMENT_CPA}
            </h3>
            <CpaConditions />
            <div
              className={styles.subDivider}
              role="separator"
              aria-hidden="true"
            />
            <h3 className={styles.subSectionTitle}>
              {AppStrings.AFFILIATE_PAYMENT_REV_SHARE}
            </h3>
            <RevShareConditions revenueSource={revenueSource} />
          </>
        )}
      </section>

      {!hideFooter && (
        <>
          <div className={styles.divider} role="separator" aria-hidden="true" />

          <div className={styles.footer}>
            <Button
              size="large"
              intent="primary"
              htmlType="submit"
              disabled={!canContinue}
              className={styles.continueButton}
              data-testid="new-affiliate-payment-continue"
            >
              {AppStrings.CONTINUE}
            </Button>
          </div>
        </>
      )}
    </Form>
  );
};

export default Payment;
